import QRCode from 'qrcode.react';
import React, { useEffect, useState } from 'react'
import { BsChatFill } from 'react-icons/bs';
import { IoIosLink } from 'react-icons/io';
import { IoCloseSharp } from 'react-icons/io5'
import { Mail } from 'react-social-sharing';
import FacebookShareButton from '../../common/FacebookShareButton';
import { useQuery } from '@apollo/client';
import { getVisibleAssociationsWithTeamsQuery } from '../../queries/association';

const { REACT_APP_BRAND } = process.env;

interface ShareWebshopProps {
  closeModal: () => void;
  shopShareDomain: string;
  associationSlug: string;
  teamSlug?: string;
  teamMemberSlug?: string;
  isMyWebshop: boolean;
}

const addHttpsIfMissing = (url: string) => {
  if (!url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
}

const ShareWebshop: React.FC<ShareWebshopProps> = ({ closeModal, shopShareDomain, associationSlug, teamSlug, teamMemberSlug, isMyWebshop }) => {
  const [isShareLinkCopied, setIsShareLinkCopied] = useState(false)
  const [shareLink, setShareLink] = useState('');

  // Check if this association is the only one on the domain (no teams) - same logic as Startpage.jsx
  // Use shopShareDomain instead of window.location.hostname to match the URLs we actually display
  const domain = shopShareDomain?.replace(/^https?:\/\//, '') || window.location.hostname;
  const { data: visibleAssociationsData } = useQuery(getVisibleAssociationsWithTeamsQuery, {
    variables: { domain },
    skip: !associationSlug
  });

  const visibleAssociations = visibleAssociationsData?.visibleAssociationsWithTeams || [];
  // Only apply logic for association-level shops
  const isOnlyAssociation = !teamSlug && !teamMemberSlug && visibleAssociations.length === 1 &&
    visibleAssociations[0]?.slug === associationSlug &&
    visibleAssociations[0]?.teams?.length === 0;

  // Set webshop share link based on slugs
  useEffect(() => {
    // Build the full path link
    let fullPath = `${addHttpsIfMissing(shopShareDomain)}/${associationSlug}`;
    if (teamSlug) {
      fullPath += `/${teamSlug}`;
    }
    if (teamMemberSlug) {
      fullPath += `/${teamMemberSlug}`;
    }

    // Set display link - use short URL if association is alone, otherwise use full path
    const displayUrl = isOnlyAssociation
      ? addHttpsIfMissing(shopShareDomain)
      : fullPath;
    setShareLink(displayUrl);
  }, [shopShareDomain, associationSlug, teamSlug, teamMemberSlug, isOnlyAssociation]);

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    setIsShareLinkCopied(true);
  }

  const handleClose = () => {
    setIsShareLinkCopied(false);
    closeModal();
  }

  const handleClickOutside = (event) => {
    if (event.target.className === 'modal share-webshop-modal') {
      handleClose();
    }
  }

  return (
    <div className="modal share-webshop-modal" onClick={handleClickOutside}>
      <div className="share-webshop box">
        <IoCloseSharp size={24} className="close" onClick={handleClose} />
        <h3>
          {isMyWebshop ? 'Dela din shop' : 'Dela webbshop'}
        </h3>
        <QRCode
          value={shareLink}
          bgColor={REACT_APP_BRAND === 'SAENKELT' ? '#EEF8F6' : '#F5F3F2'}
          includeMargin={true}
          size={200}
        />
        <span>
          {isMyWebshop ? 'Denna QR-kod leder till din webbshop' : 'Denna QR-kod leder till webbshoppen'}
        </span>
        <ul className='share-buttons flex items-center'>
          <li><FacebookShareButton link={shareLink} /></li>
          <li className='mail'><Mail link={shareLink} /></li>
          <li><a className='sms' href={`sms:?&body=Min%20Webbshop!%20${shareLink}`} title=''>
            <BsChatFill size={24} /><span>SMS</span>
          </a></li>
          <li><a className='sms copy c-pointer' onClick={copyShareLink} title=''>
            <IoIosLink size={24} /><span>{isShareLinkCopied ? 'Kopierad!' : 'Kopiera'}</span>
          </a></li>
        </ul>
      </div>
    </div>
  )
}

export default ShareWebshop
